import { Megaphone, TrendingUp, Target, Users, DollarSign } from "lucide-react";

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const mxn = (v) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 0 });

const COLS = [
  { key: "planeada", label: "Planeadas", dot: "bg-navy/30" },
  { key: "activa", label: "Activas", dot: "bg-violet" },
  { key: "finalizada", label: "Finalizadas", dot: "bg-emerald-500" },
];

const CHANNEL_TONE = {
  "Email": "bg-violet/10 text-violet",
  "Redes sociales": "bg-sky-500/10 text-sky-600",
  "WhatsApp": "bg-emerald-500/10 text-emerald-600",
  "SMS": "bg-amber-500/10 text-amber-600",
  "Push": "bg-fuchsia-500/10 text-fuchsia-600",
  "Eventos": "bg-rose-500/10 text-rose-600",
  "Publicidad": "bg-indigo-500/10 text-indigo-600",
};

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-surface p-4" data-testid={`camp-metric-${label}`}>
      <div className="flex items-center gap-1.5 text-xs text-navy/50"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className={`mt-1 font-editorial text-3xl ${accent || "text-navy/90"}`}>{value}</div>
    </div>
  );
}

function Card({ it, onOpen }) {
  const roi = num(it.presupuesto) > 0 ? Math.round((num(it.ingresos) / num(it.presupuesto)) * 100) : 0;
  const tone = CHANNEL_TONE[it.canal] || "bg-navy/10 text-navy/60";
  return (
    <button onClick={() => onOpen(it)} data-testid={`camp-card-${it.id}`}
      className="w-full text-left card-surface p-4 hover:border-violet/40 hover:shadow-md transition-all group">
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-navy/90 leading-snug group-hover:text-violet transition-colors">{it.nombre}</div>
        {it.canal && <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${tone}`}>{it.canal}</span>}
      </div>
      {it.objetivo && <div className="mt-1 text-xs text-navy/45 line-clamp-2">{it.objetivo}</div>}
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div><div className="text-[10px] text-navy/40 uppercase tracking-wide">Leads</div><div className="text-sm font-medium text-navy/80">{mxn(num(it.leads))}</div></div>
        <div><div className="text-[10px] text-navy/40 uppercase tracking-wide">Conv.</div><div className="text-sm font-medium text-navy/80">{mxn(num(it.conversiones))}</div></div>
        <div><div className="text-[10px] text-navy/40 uppercase tracking-wide">ROI</div><div className={`text-sm font-medium ${roi >= 100 ? "text-emerald-600" : "text-navy/80"}`}>{roi}%</div></div>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-navy/45">Presup. ${mxn(num(it.presupuesto))}</span>
        <span className="text-navy/70 font-medium">Ing. ${mxn(num(it.ingresos))}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-navy/10 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${roi >= 100 ? "bg-emerald-500" : "bg-violet"}`} style={{ width: `${Math.min(roi, 100)}%` }} />
      </div>
    </button>
  );
}

export default function ModuleCampaigns({ items, onOpen }) {
  const budget = items.reduce((a, b) => a + num(b.presupuesto), 0);
  const leads = items.reduce((a, b) => a + num(b.leads), 0);
  const conv = items.reduce((a, b) => a + num(b.conversiones), 0);
  const rev = items.reduce((a, b) => a + num(b.ingresos), 0);
  const roi = budget > 0 ? Math.round((rev / budget) * 100) : 0;

  const groups = COLS.map((c) => ({ ...c, list: items.filter((it) => (it.estado || "planeada") === c.key) }));

  return (
    <div className="space-y-5" data-testid="module-campaigns">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
        <Metric icon={Megaphone} label="Campañas" value={items.length} />
        <Metric icon={DollarSign} label="Presupuesto" value={`$${mxn(budget)}`} />
        <Metric icon={Users} label="Leads" value={mxn(leads)} />
        <Metric icon={Target} label="Conversiones" value={mxn(conv)} />
        <Metric icon={TrendingUp} label="ROI total" value={`${roi}%`} accent={roi >= 100 ? "text-emerald-600" : "text-violet"} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {groups.map((g) => (
          <div key={g.key} className="space-y-3" data-testid={`camp-col-${g.key}`}>
            <div className="flex items-center gap-2 px-1">
              <span className={`w-2 h-2 rounded-full ${g.dot}`} />
              <span className="text-sm font-medium text-navy/70">{g.label}</span>
              <span className="text-xs text-navy/35">{g.list.length}</span>
            </div>
            <div className="space-y-3">
              {g.list.length === 0
                ? <div className="text-xs text-navy/30 px-1 py-6 text-center border border-dashed border-navy/10 rounded-lg">Sin campañas</div>
                : g.list.map((it) => <Card key={it.id} it={it} onOpen={onOpen} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
